import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { T } from "@/content/site";

function formatNumber(n) {
  return new Intl.NumberFormat("en-US").format(n);
}

function Counter({ target, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const duration = 1800;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <span ref={ref} className="font-serif text-white text-[44px] sm:text-[56px] md:text-[64px] lg:text-[76px] leading-[0.95] tracking-[-0.02em] tabular-nums whitespace-nowrap block">
      {formatNumber(value)}
      <span className="text-[color:var(--sacrvm-gold)]">{suffix}</span>
    </span>
  );
}

export default function Counters() {
  const { t } = useLang();
  return (
    <section id="counters" data-testid="counters-section" className="relative bg-[#0F0F0F] py-[100px] md:py-[140px] border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="overline mb-14">
          <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
          {t(T.counters.overline)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-14 md:gap-6">
          {T.counters.items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: i * 0.08 }}
              className="md:border-l md:border-white/10 md:pl-8 first:border-l-0 first:pl-0"
              data-testid={`counter-${i}`}
            >
              <Counter target={item.n} suffix={item.suffix} />
              <div className="mt-5 text-[color:var(--sacrvm-text-muted)] text-[13px] tracking-[0.24em] uppercase">
                {t(item.label)}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
